/**
 * SyncFreshness — the quiet "Synced <relative-time>" indicator shown on every
 * surface that the auto-sync ticker feeds (Templates, Customer Templates, Mockup
 * Generator). It reads `getLastSyncAt()` and re-reads it on each delivery + on a
 * slow 30s timer so the relative text stays honest without hammering renders.
 *
 * Deliberately unobtrusive: a `.faint` span, `aria-live="polite"`, testid
 * `sync-freshness`. While a revalidation tick is actually in flight it shows a small
 * `.btn-spinner` next to the text (a live activity indicator, replacing the old
 * silent-wait), which disappears the moment the tick settles.
 */
import { useEffect, useState } from "react";
import { getLastSyncAt, getSyncing, subscribeSyncState, subscribeTemplates } from "./autoSync.js";

/** Human relative freshness. `null` (never synced) reads as "Not synced yet". */
export function freshnessLabel(at: number | null, now: number): string {
  if (at == null) return "Not synced yet";
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return "Synced just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `Synced ${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `Synced ${h}h ago`;
  const d = Math.floor(h / 24);
  return `Synced ${d}d ago`;
}

export function SyncFreshness({ className }: { className?: string }) {
  const [at, setAt] = useState<number | null>(() => getLastSyncAt());
  const [now, setNow] = useState<number>(() => Date.now());
  const [syncing, setSyncing] = useState<boolean>(() => getSyncing());

  useEffect(() => {
    // Snap to the latest on delivery (immediate feedback when a change lands)…
    const unsub = subscribeTemplates((d) => {
      setAt(d.at);
      setNow(Date.now());
    });
    // …reflect the live tick activity (spinner on/off) as the ticker starts/settles, AND
    // re-read the clock on the same channel. WS-B B-6 / F-14: `noteManualSync()` stamps
    // `lastSyncAt` when the PAGE refreshes itself and notifies here, so pressing Refresh
    // moves this line instead of leaving it saying "Synced 4m ago" beside rows that were
    // just re-pulled — the one thing this line exists to be right about.
    const unsubSync = subscribeSyncState(() => {
      setSyncing(getSyncing());
      setAt(getLastSyncAt());
      setNow(Date.now());
    });
    // …and refresh the relative text (and any unchanged-tick freshness) on a slow timer.
    const id = setInterval(() => {
      setAt(getLastSyncAt());
      setNow(Date.now());
    }, 30_000);
    return () => {
      unsub();
      unsubSync();
      clearInterval(id);
    };
  }, []);

  return (
    <span
      className={`faint sync-freshness${className ? ` ${className}` : ""}`}
      data-testid="sync-freshness"
      aria-live="polite"
      aria-busy={syncing}
      title="Templates auto-sync every 60 seconds and on window focus"
    >
      {syncing && <span className="btn-spinner" aria-hidden="true" data-testid="sync-spinner" />}
      {freshnessLabel(at, now)}
    </span>
  );
}
